import api from '../utils/api';

const fileService = {
  // List files and folders in a dataroom
  listFiles: async (dataroomUuid, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add optional parameters
      if (params.folder_uuid) queryParams.append('folder_uuid', params.folder_uuid);
      if (params.search) queryParams.append('search', params.search);
      if (params.type) queryParams.append('type', params.type);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      // Add file_types[] array parameters
      if (params.file_types && Array.isArray(params.file_types)) {
        params.file_types.forEach(type => {
          queryParams.append('file_types[]', type);
        });
      }

      // Add cache-busting parameter - use random number to ensure uniqueness
      if (params.forceRefresh) {
        queryParams.append('_t', Date.now());
        queryParams.append('_r', Math.random().toString(36).substring(7));
      }

      const url = `/datarooms/${dataroomUuid}/files?${queryParams.toString()}`;
      console.log('🌐 API Request URL:', url);

      // Add additional cache-busting headers for force refresh
      const config = {};
      if (params.forceRefresh) {
        config.headers = {
          'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
          'Pragma': 'no-cache',
          'Expires': '0',
          'If-Modified-Since': '0',
          'If-None-Match': '*'
        };
      }

      const response = await api.get(url, config);

      console.log('✅ API Response:', response.data);
      console.log('✅ Files/Folders count:', (response.data.items || response.data.data || response.data.files || []).length);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Search files in a dataroom
  searchFiles: async (dataroomUuid, searchQuery) => {
    try {
      const queryParams = new URLSearchParams();
      if (searchQuery) {
        queryParams.append('search', searchQuery);
      }

      const url = `/datarooms/${dataroomUuid}/files/search?${queryParams.toString()}`;
      console.log('🔎 fileService.searchFiles - Making request to:', url);
      console.log('🔎 fileService.searchFiles - Search query:', searchQuery);

      const response = await api.get(url);

      console.log('✅ fileService.searchFiles - Response received:', response);
      console.log('✅ fileService.searchFiles - Response data:', response.data);

      return response.data;
    } catch (error) {
      console.error('❌ fileService.searchFiles - Error:', error);
      console.error('❌ fileService.searchFiles - Error response:', error.response);
      throw error;
    }
  },

  // Upload file
  uploadFile: async (dataroomUuid, file, parentFolderUuid = null, description = '', onUploadProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (parentFolderUuid) {
        formData.append('parent_folder_uuid', parentFolderUuid);
      }
      if (description) {
        formData.append('description', description);
      }

      // Let axios/browser set Content-Type automatically for FormData
      const response = await api.post(`/datarooms/${dataroomUuid}/files/upload`, formData, {
        headers: {
          'Content-Type': undefined  // This tells axios to set it automatically
        },
        onUploadProgress: onUploadProgress
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Bulk upload files with folder hierarchy
  bulkUploadWithFolders: async (dataroomUuid, files, parentFolderUuid = null, emptyFolders = [], onUploadProgress) => {
    try {
      const formData = new FormData();

      // Add parent folder UUID if provided
      if (parentFolderUuid) {
        formData.append('parent_folder_uuid', parentFolderUuid);
        console.log('📁 Parent folder UUID:', parentFolderUuid);
      }

      // Add all files with their relative paths
      files.forEach((fileItem, index) => {
        formData.append(`files[${index}]`, fileItem.file);

        // Add the relative path for each file (this tells backend the folder structure)
        if (fileItem.path) {
          formData.append(`paths[${index}]`, fileItem.path);
        }
      });

      console.log('📄 Total files in FormData:', files.length);

      // Add empty folders as JSON array
      if (emptyFolders.length > 0) {
        formData.append('empty_folders', JSON.stringify(emptyFolders));
        console.log('📂 Empty folders being sent:', emptyFolders);
      }

      // Send to bulk upload endpoint
      const uploadUrl = `/datarooms/${dataroomUuid}/files/bulk-upload`;
      console.log('🌐 Uploading to:', uploadUrl);

      const response = await api.post(uploadUrl, formData, {
        headers: {
          'Content-Type': undefined  // Let axios set it automatically
        },
        onUploadProgress: onUploadProgress
      });

      console.log('✅ Upload successful! Response:', response.data);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get import progress for bulk upload
  getImportProgress: async (dataroomUuid, importJobUuid) => {
    try {
      const response = await api.get(
        `/datarooms/${dataroomUuid}/files/bulk-upload/${importJobUuid}/progress`
      );

      console.log('📊 Import progress:', response.data);

      return response.data;
    } catch (error) {
      console.error('❌ Failed to get import progress:', error);
      throw error;
    }
  },

  // Create folder
  createFolder: async (dataroomUuid, name, parentFolderUuid = null, description = '') => {
    try {
      const data = {
        name: name,
        parent_folder_uuid: parentFolderUuid,
        description: description
      };

      const response = await api.post(`/datarooms/${dataroomUuid}/files/folders`, data);


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // View file - returns the URL from API response
  viewFile: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/view`);
      const data = response.data;


      // Use the 'url' field from the API response
      const viewUrl = data.url || data.view_url || data.data?.url || data.data?.view_url;
      const mimeType = data.mime_type || data.data?.mime_type || 'application/octet-stream';

      return {
        ...data,
        url: viewUrl,
        view_url: viewUrl,
        mime_type: mimeType,
        mimeType: mimeType,
        isBlobUrl: false
      };
    } catch (error) {
      throw error;
    }
  },

  // Preview file - returns the preview URL from API response
  previewFile: async (dataroomUuid, fileUuid) => {
    try {
      // The preview endpoint returns the file content directly as a blob
      const response = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/preview`, {
        responseType: 'blob'
      });

      // Get the content type from response headers
      const mimeType = response.headers['content-type'] || 'application/octet-stream';

      // Create a blob URL from the response
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);

      console.log('Preview blob created:', blobUrl, 'MIME type:', mimeType);

      return {
        url: blobUrl,
        preview_url: blobUrl,
        mime_type: mimeType,
        mimeType: mimeType,
        isBlobUrl: true,
        blob: blob
      };
    } catch (error) {
      console.error('Preview file error:', error);
      throw error;
    }
  },


  // Get download info with proxy_download_url
  getDownloadInfo: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/download?download_url=true`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download file - Returns response with proxy_download_url if available
  downloadFile: async (dataroomUuid, fileUuid) => {
    try {
      // First try to get download info with proxy_download_url
      const response = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/download`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      // Check if response contains proxy_download_url
      if (response.data && (response.data.proxy_download_url || response.data.url)) {
        return response.data;
      }
      
      // If not, fall back to blob download
      const blobResponse = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/download`, {
        responseType: 'blob',
        headers: {
          'Accept': '*/*'
        },
        timeout: 120000 // 2 minutes for large files
      });
      
      return blobResponse.data;
    } catch (error) {
      
      if (error.response?.status === 404) {
        throw new Error('File not found');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied');
      }
      
      throw error;
    }
  },
  
  // Alternative download method using direct URL
  getDownloadUrl: (dataroomUuid, fileUuid) => {
    const token = localStorage.getItem('token');
    const baseURL = api.defaults.baseURL || '';
    return `${baseURL}/datarooms/${dataroomUuid}/files/${fileUuid}/download?token=${token}`;
  },
  

  // Delete file
  // Delete single file using bulk endpoint
  deleteFile: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/delete`, {
        uuids: [fileUuid]
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete multiple files using bulk endpoint
  deleteMultipleFiles: async (dataroomUuid, fileUuids) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/delete`, {
        uuids: fileUuids
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Rename file
  renameFile: async (dataroomUuid, fileUuid, newName) => {
    try {
      const response = await api.patch(`/datarooms/${dataroomUuid}/files/${fileUuid}/rename`, {
        new_name: newName  // API expects new_name field as per Postman collection
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Copy file
  copyFile: async (dataroomUuid, fileUuid, destinationFolderUuid = null) => {
    try {
      const data = {
        destination_folder_uuid: destinationFolderUuid || null
      };

      const response = await api.post(`/datarooms/${dataroomUuid}/files/${fileUuid}/copy`, data);


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Move file
  moveFile: async (dataroomUuid, fileUuid, destinationFolderUuid = null) => {
    try {
      const data = {
        destination_folder_uuid: destinationFolderUuid || null
      };

      const response = await api.patch(`/datarooms/${dataroomUuid}/files/${fileUuid}/move`, data);


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Assign tag to file
  assignTagToFile: async (dataroomUuid, fileUuid, tagId) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/${fileUuid}/tags/assign`, {
        tag_id: tagId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Remove tag from file
  removeTagFromFile: async (dataroomUuid, fileUuid, tagId) => {
    try {
      const response = await api.delete(`/datarooms/${dataroomUuid}/files/${fileUuid}/tags/${tagId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get file access logs
  getFileAccessLogs: async (dataroomUuid, fileUuid, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add optional parameters
      if (params.action) queryParams.append('action', params.action);
      if (params.user_id) queryParams.append('user_id', params.user_id);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);
      
      const response = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/access-logs?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get file statistics
  getFileStatistics: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/access-stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark file as read
  markAsRead: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/${fileUuid}/read`);


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark file as unread
  markAsUnread: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/${fileUuid}/unread`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark multiple files as read (bulk)
  markAsReadBulk: async (dataroomUuid, fileUuids) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/mark-read`, {
        file_uuids: fileUuids
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark multiple files as unread (bulk)
  markAsUnreadBulk: async (dataroomUuid, fileUuids) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/mark-unread`, {
        file_uuids: fileUuids
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add file to favorites
  addToFavorite: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/${fileUuid}/favorite`);


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Remove file from favorites
  removeFromFavorite: async (dataroomUuid, fileUuid) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/${fileUuid}/unfavorite`);


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add multiple files to favorites (bulk)
  addToFavoriteBulk: async (dataroomUuid, fileUuids) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/mark-favorite`, {
        file_uuids: fileUuids
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Remove multiple files from favorites (bulk)
  removeFromFavoriteBulk: async (dataroomUuid, fileUuids) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/files/mark-unfavorite`, {
        file_uuids: fileUuids
      });


      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toggle watermark status for dataroom
  toggleWatermark: async (dataroomUuid, enabled) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/watermark/toggle`, {
        enabled: enabled
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Helper function to trigger file download
  triggerDownload: async (dataroomUuid, fileUuid, filename) => {
    try {
      const response = await fileService.downloadFile(dataroomUuid, fileUuid);
      
      // Check if response is already a blob
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else if (response.proxy_download_url) {
        // If we have proxy_download_url, use it directly for download
        const link = document.createElement('a');
        link.href = response.proxy_download_url;
        link.download = filename;
        link.style.display = 'none';

        // Add auth token if needed
        if (!response.proxy_download_url.includes('token=')) {
          const token = localStorage.getItem('token');
          link.href = `${response.proxy_download_url}${response.proxy_download_url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (response.url) {
        // Fallback to direct URL for download
        const link = document.createElement('a');
        link.href = response.url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('No download URL available');
      }
    } catch (error) {
      throw error;
    }
  },

  // Helper function to format file size
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // List folders API endpoint for copy/move operations
  listFolders: async (dataroomUuid) => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/folders`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get folders with file counts for tree structure
  getFoldersWithFileCounts: async (dataroomUuid) => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/folders/with-files-counts`);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all folders for copy/move operations
  getAllFolders: async (dataroomUuid) => {
    try {
      
      // Use the same listFiles method to ensure consistency
      const response = await fileService.listFiles(dataroomUuid, {
        type: 'folder' // Only get folders
      });
      
      
      // Handle the response structure - same as fetchFiles in FileManager
      const allFolders = response.data || response.files || [];
      
      
      // Add level property for UI rendering (flat structure for now)
      const foldersWithLevel = allFolders.map(folder => ({
        ...folder,
        level: 0
      }));
      
      return { data: foldersWithLevel };
    } catch (error) {
      throw error;
    }
  },

  // Proxy method to fetch file through backend to avoid CORS
  fetchFileThroughProxy: async (dataroomUuid, fileUuid, publicUrl) => {
    try {
      
      // Create a proxy request through your backend
      const response = await api.post(`/datarooms/${dataroomUuid}/files/${fileUuid}/proxy`, {
        url: publicUrl
      }, {
        responseType: 'blob'
      });
      
      return response.data;
    } catch (error) {
      
      // If proxy fails, try alternative approach
      // Call the backend to get a signed URL or proxy URL
      try {
        const urlResponse = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/proxy-url`);
        if (urlResponse.data.proxy_url) {
          // Fetch from the proxy URL provided by backend
          const fileResponse = await fetch(urlResponse.data.proxy_url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (fileResponse.ok) {
            return await fileResponse.blob();
          }
        }
      } catch (proxyError) {
      }
      
      throw error;
    }
  },

  // Helper function to get file icon based on type
  getFileIcon: (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: 'fa-file-pdf',
      doc: 'fa-file-word',
      docx: 'fa-file-word',
      xls: 'fa-file-excel',
      xlsx: 'fa-file-excel',
      ppt: 'fa-file-powerpoint',
      pptx: 'fa-file-powerpoint',
      jpg: 'fa-file-image',
      jpeg: 'fa-file-image',
      png: 'fa-file-image',
      gif: 'fa-file-image',
      zip: 'fa-file-archive',
      rar: 'fa-file-archive',
      mp4: 'fa-file-video',
      avi: 'fa-file-video',
      mp3: 'fa-file-audio',
      wav: 'fa-file-audio',
      txt: 'fa-file-alt',
      csv: 'fa-file-csv'
    };
    return iconMap[extension] || 'fa-file';
  },

  // Export dataroom inventory/index
  exportDataroomIndex: async (dataroomUuid, format = 'excel') => {
    try {
      const response = await api.get(`/datarooms/${dataroomUuid}/files/export/inventory`, {
        params: { format },
        responseType: 'blob'
      });

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `dataroom_index_${new Date().toISOString().split('T')[0]}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      throw error;
    }
  },

  // Get favorite files
  getFavoriteFiles: async (dataroomUuid, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add optional parameters
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);

      const response = await api.get(`/datarooms/${dataroomUuid}/files/favorites?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get recently opened files
  getRecentlyOpenedFiles: async (dataroomUuid, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add optional parameters
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const response = await api.get(`/datarooms/${dataroomUuid}/files/recently-opened?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get recently uploaded files
  getRecentlyUploadedFiles: async (dataroomUuid, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add optional parameters
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const response = await api.get(`/datarooms/${dataroomUuid}/files/recently-uploaded?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Export dataroom files by scope
  exportFilesByScope: async (dataroomUuid, scope = 'all') => {
    try {
      const validScopes = ['all', 'recently_uploaded', 'recently_opened', 'favorites'];

      if (!validScopes.includes(scope)) {
        throw new Error(`Invalid scope: ${scope}. Valid scopes are: ${validScopes.join(', ')}`);
      }

      let url = `/datarooms/${dataroomUuid}/files/export`;

      // Add scope parameter if not 'all'
      if (scope !== 'all') {
        url += `?scope=${scope}`;
      }

      const response = await api.get(url, {
        responseType: 'blob', // Important for file downloads
        headers: {
          'Accept': 'application/vnd.ms-excel'
        }
      });

      // Create a download link
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.ms-excel'
      });
      const downloadUrl = window.URL.createObjectURL(blob);

      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `dataroom_export_${scope}_${Date.now()}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, filename };
    } catch (error) {
      throw error;
    }
  }
};

export default fileService;